import clsx from 'clsx';
import React from 'react';
import PropTypes from 'prop-types';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { ArrowForward } from '@mui/icons-material';
import { Box, Container, Typography, Grid, Button } from '@mui/material';
import { useStyles } from './styles';
import { flowTrialActive } from 'utils';

const Props = {
  onActionClick: PropTypes.func,
};

export default function MainContent({ onActionClick }) {
  const classes = useStyles();
  const router = useRouter();

  return (
    <>
      <Container className={classes.negativeContainer}>
        <div className={classes.greySection}>
          <Box mb={4}>
            <img src="/images/arrow-drop-icon-secondary.svg" alt="Arrow drop icon" />
          </Box>

          <Typography>
            When we talk about communicating science through visuals, <b>Biorender</b> is usually{' '}
            <br />
            the first option that comes to mind and it can deliver great results as a fast
            scientific <br />
            communication tool. However, a Free version with many obstacles, such as:
          </Typography>

          <Box my={6} px={15}>
            <Grid container={true} spacing={2}>
              <Grid item={true} xs={12} md={4}>
                <img
                  className={classes.icon}
                  src="/images/few-icon-editing-icon.svg"
                  alt="Icon editing icon"
                />

                <Typography>
                  Few icon editing tools <br />
                  and lack of permission <br />
                  to publish in journals
                </Typography>
              </Grid>

              <Grid item={true} xs={12} md={4}>
                <img
                  className={classes.icon}
                  src="/images/high-costs-icon.svg"
                  alt="High costs icon"
                />

                <Typography>
                  The high costs <br />
                  for subscriptions (starting <br />
                  from 420 USD per year)
                </Typography>
              </Grid>

              <Grid item={true} xs={12} md={4}>
                <img
                  className={classes.icon}
                  src="/images/icon-limitation-icon.svg"
                  alt="Icon limitation icon"
                />

                <Typography>
                  A maximum of 3 <br />
                  custom icons <br />
                  per year
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Typography className={classes.secondaryColor} variant="h4">
            <b>
              Check some Biorender <br />
              alternatives below.
            </b>
          </Typography>
        </div>

        <div className={classes.primarySection}>
          <Box mb={4}>
            <img src="/images/arrow-drop-icon-white.svg" alt="Arrow drop icon" />
          </Box>

          <Typography variant="h4">
            <b>Mind the Graph</b>
          </Typography>

          <img
            className={classes.sectionImage}
            src="/images/mindthegraph-banner.png"
            alt="Mind the Graph banner"
          />

          <Box mt={2} mb={4}>
            <Typography>
              With a proposal very similar to Biorender, <b>Mind the Graph</b> aims to Create
              effective science figures in <br />
              minutes. The extensive library with +40,000 icons, is easily expanded by accepting an
              incredible amount <br />
              of new user requests, giving emphasis to what needs to be seen in a scientific figure.
            </Typography>
          </Box>

          <Box mb={6} px={15}>
            <Grid container={true} spacing={2}>
              <Grid item={true} xs={12} md={4}>
                <img className={classes.icon} src="/images/freemium-icon.svg" alt="Freemium icon" />

                <Typography>
                  <b>Freemium version</b>
                </Typography>
                <Typography>
                  With no time limit, <br />
                  allowing publication <br />
                  in Journals
                </Typography>
              </Grid>

              <Grid item={true} xs={12} md={4}>
                <img
                  className={classes.icon}
                  src="/images/trial-subscription-icon.svg"
                  alt="Trial subscription icon"
                />

                <Typography>
                  <b>7-day trial as a paid subscriber</b>
                </Typography>
                <Typography>
                  Here, what stands out is that you <br />
                  can request up to 5 custom illustrations <br />
                  for your research
                </Typography>
              </Grid>

              <Grid item={true} xs={12} md={4}>
                <img
                  className={classes.icon}
                  src="/images/icon-editing-icon.svg"
                  alt="Icon editing icon"
                />

                <Typography>
                  <b>Icon editing tools</b>
                </Typography>
                <Typography>
                  In both Freemium <br />
                  and Premium <br />
                  versions;
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Box mb={6} px={10}>
            <Grid container={true} spacing={2}>
              <Grid item={true} xs={12} md={4}>
                <img
                  className={classes.icon}
                  src="/images/stunning-templates-icon.svg"
                  alt="Stunning templates icon"
                />

                <Typography>
                  <b>200+ stunning templates</b>
                </Typography>
                <Typography>
                  Several custom and editable <br />
                  templates that can save <br />
                  many hours of work
                </Typography>
              </Grid>

              <Grid item={true} xs={12} md={4}>
                <img
                  className={classes.icon}
                  src="/images/price-catches-icon.svg"
                  alt="Price catches icon"
                />

                <Typography>
                  <b>The price catches the eye here</b>
                </Typography>
                <Typography>
                  Starting from 7 dollars a month. Including custom illustrations <br />
                  for your research
                </Typography>
              </Grid>

              <Grid item={true} xs={12} md={4}>
                <img
                  className={classes.icon}
                  src="/images/poster-maker-feature-icon.svg"
                  alt="Poster maker feature icon"
                />

                <Typography>
                  <b>Poster Maker</b>
                </Typography>
                <Typography>
                  Scientific posters in a few clicks: <br />
                  submit your article, and voila <br />- you got a brand new poster!
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <NextLink
            href={`/auth/login-register?from_url=${router.basePath + router.asPath}`}
            passHref={true}
          >
            <Button
              className={clsx(classes.button, classes.secondaryButton)}
              color="primary"
              variant="contained"
              onClick={onActionClick}
            >
              Try Mind the Graph Now!
            </Button>
          </NextLink>
        </div>
      </Container>

      <Box mt={-25} py={6} bgcolor="common.white">
        <Container>
          <Typography className={classes.primaryTitle} variant="h4">
            <b>Other not so related Biorender alternatives</b>
          </Typography>

          <Grid container={true} spacing={2} alignItems="center">
            <Grid item={true} xs={12} md={6}>
              <Box px={6}>
                <Typography className={classes.sectionTitle} variant="h5" component="h2">
                  <ArrowForward />

                  <b>Animate your science</b>
                </Typography>

                <Box mt={2} mb={3}>
                  <Typography>
                    Animate Your Science is an Australian communication agency for researchers and
                    institutions. It creates animated video abstracts, custom science animations and
                    graphics, scientific posters, and graphical abstracts for scientific research on
                    demand.
                  </Typography>

                  <Typography>
                    <b>Some things to keep in mind:</b>
                  </Typography>
                </Box>

                <ul className={classes.list}>
                  <li>
                    <span>Animated options</span>
                  </li>
                  <li>
                    <span>Custom designs of Graphical Abstracts and Scientific Posters</span>
                  </li>
                  <li>
                    <span>Average delivery time 1-2 weeks</span>
                  </li>
                  <li>
                    <span>Includes Copyright of creations</span>
                  </li>
                  <li>
                    <span>
                      Quotes start at 440 USD for Graphical Abstracts and 2,190 USD for animated
                      videos
                    </span>
                  </li>
                  <li>
                    <span>Extra fees if you're not located in Australia</span>
                  </li>
                  <li>
                    <span>No creation studio for users to edit the graphics if needed</span>
                  </li>
                </ul>
              </Box>
            </Grid>

            <Grid item={true} xs={12} md={6}>
              <img
                className={classes.sectionImage}
                src="/images/animate-your-science-alternative.png"
                alt="Animate Your Science alternative"
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box py={6} bgcolor="grey.200">
        <Container>
          <Grid container={true} spacing={2} alignItems="center">
            <Grid item={true} xs={12} md={6}>
              <img
                className={classes.sectionImage}
                src="/images/somersault-alternative.png"
                alt="Somersault alternative"
              />
            </Grid>

            <Grid item={true} xs={12} md={6}>
              <Box px={6}>
                <Typography className={classes.sectionTitle} variant="h5" component="h2">
                  <ArrowForward />

                  <b>Somersault</b>
                </Typography>

                <Box mt={2} mb={3}>
                  <Typography>
                    Somersault 18:24 is a Belgium-based design agency that creates custom Graphical
                    Abstracts and Publication Figures on demand.
                  </Typography>

                  <Typography>
                    <b>Some things to keep in mind:</b>
                  </Typography>
                </Box>

                <ul className={classes.list}>
                  <li>
                    <span>Custom designs</span>
                  </li>
                  <li>
                    <span>No average price quote informed</span>
                  </li>
                  <li>
                    <span>
                      No creation studio for users to edit something on their graphics if needed
                    </span>
                  </li>
                </ul>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box py={6} bgcolor="common.white">
        <Container>
          <Grid container={true} spacing={2} alignItems="center">
            <Grid item={true} xs={12} md={6}>
              <Box px={6}>
                <Typography className={classes.sectionTitle} variant="h5" component="h2">
                  <ArrowForward />

                  <b>Bioicons</b>
                </Typography>

                <Box mt={2} mb={3}>
                  <Typography>
                    Bioicons is a gallery of bio-related icons that gatherers illustrations from
                    other sources in one place so you can download as SVG files.
                  </Typography>

                  <Typography>
                    <b>Some things to keep in mind:</b>
                  </Typography>
                </Box>

                <ul className={classes.list}>
                  <li>
                    <span>Free</span>
                  </li>
                  <li>
                    <span>Vector icons</span>
                  </li>
                  <li>
                    <span>Attribution to creator needed for each icon</span>
                  </li>
                  <li>
                    <span>No creation studio to elaborate designs</span>
                  </li>
                  <li>
                    <span>No custom illustrations or specific visuals</span>
                  </li>
                </ul>
              </Box>
            </Grid>

            <Grid item={true} xs={12} md={6}>
              <img
                className={classes.sectionImage}
                src="/images/bioicons-alternative.png"
                alt="Bioicons alternative"
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box py={6} bgcolor="grey.200">
        <Container>
          <Grid container={true} spacing={2} alignItems="center">
            <Grid item={true} xs={12} md={6}>
              <img
                className={classes.sectionImage}
                src="/images/canva-alternative.png"
                alt="Canva alternative"
              />
            </Grid>

            <Grid item={true} xs={12} md={6}>
              <Box px={6}>
                <Typography className={classes.sectionTitle} variant="h5" component="h2">
                  <ArrowForward />

                  <b>Canva</b>
                </Typography>

                <Box mt={2} mb={3}>
                  <Typography>
                    Canva is an Australian graphic design platform, used to create social media{' '}
                    graphics, presentations, posters, documents, and other visual content that may{' '}
                    be used by scientists with more design skills to create scientific visuals.
                  </Typography>

                  <Typography>
                    <b>Some things to keep in mind:</b>
                  </Typography>
                </Box>

                <ul className={classes.list}>
                  <li>
                    <span>Free subscription or US$12,99 for pro benefits</span>
                  </li>
                  <li>
                    <span>Limited gallery of copyright included arts</span>
                  </li>
                  <li>
                    <span>Accessible for users with fewer design skills</span>
                  </li>
                  <li>
                    <span>60 USD fee for additional users</span>
                  </li>
                  <li>
                    <span>Pro images watermarked</span>
                  </li>
                  <li>
                    <span>No scientifically accurate images</span>
                  </li>
                </ul>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box py={6} bgcolor="common.white">
        <Container>
          <Grid container={true} spacing={2} alignItems="center">
            <Grid item={true} xs={12} md={6}>
              <Box px={6}>
                <Typography className={classes.sectionTitle} variant="h5" component="h2">
                  <ArrowForward />

                  <b>PPT and Google Images</b>
                </Typography>

                <Box mt={2} mb={3}>
                  <Typography>
                    Many scientists still use Google Images and PowerPoint to illustrate their work
                    and presentations.
                  </Typography>

                  <Typography>
                    However, with copyright issues, unprofessional and unpatterned images, these{' '}
                    options may not be scientifically accurate or precise.
                  </Typography>
                </Box>

                <Typography>
                  Moreover, if your research is brand new, you'll have a hard time searching for
                  illustrations on these platforms.
                </Typography>
              </Box>
            </Grid>

            <Grid item={true} xs={12} md={6}>
              <img
                className={classes.sectionImage}
                src="/images/ppt-google-images-alternative.png"
                alt="PPT and Google Images alternative"
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box className={classes.orangeBg} py={10} textAlign="center">
        <Container>
          <Typography className={classes.whiteColor}>
            As you can see, Biorender it's not the only option for undergraduates, researchers,
            labs, etc.
            <br />
            There are custom tools, cheaper and with a different interface, that may be the best
            option for you.
          </Typography>

          <Box my={4}>
            <Typography className={classes.whiteColor} variant="h4">
              <b>
                Now that you have chosen,
                <br />
                let's get the work started.
              </b>
            </Typography>
          </Box>

          <NextLink
            href={`/auth/login-register?from_url=${router.basePath + router.asPath}`}
            passHref={true}
          >
            <Button
              className={clsx(classes.button, classes.whiteButton)}
              color="primary"
              variant="contained"
              onClick={onActionClick}
            >
              {flowTrialActive ? 'Get Started Now' : 'Sign Up For Free'}
            </Button>
          </NextLink>
        </Container>
      </Box>
    </>
  );
}

MainContent.propTypes = Props;
